import React from 'react';
import { Box, Flex, DeprecatedIcon, Typography, Text } from '@fullstory/components';
import { SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import css from './index.module.scss';
import { ArchiveIndicator } from '../ArchiveIndicator';
import { LIBRARY_OBJECT_TOOLTIP_VARIANT_STYLES } from '../../constants/objects';
import { identity } from 'lodash';

type SingleSegmentWrapper = (
  node: React.ReactNode,
  segment: SegmentDetail,
  i: number,
  segmentLink?: (segment: SegmentDetail, index: number) => string,
) => React.ReactNode;

interface SegmentNameProps {
  segments: SegmentDetail[];
  variant?: 'default' | 'minimal' | 'home' | 'homeWithIcon';
  textVariant?: 'default' | 'inverse' | 'dark';
  segmentLink?: (segment: SegmentDetail, index: number) => string;
  // TODO (timchin): remove when packaged
  wrapSingleSegment?: SingleSegmentWrapper;
  marginTop?: React.ComponentProps<typeof Box>['marginTop'];
  ellipsize?: boolean;
}

export const SegmentName: React.FC<SegmentNameProps> = function SegmentName({
  segments,
  variant = 'default',
  textVariant = 'default',
  segmentLink,
  wrapSingleSegment,
  marginTop = 4,
  ellipsize = false,
}) {
  const textColor =
    textVariant === 'dark'
      ? 'neutrals-n10'
      : textVariant === 'inverse'
      ? 'neutrals-n0'
      : 'neutrals-n7';

  const iconColor = textVariant === 'inverse' ? 'neutrals-n0' : 'neutrals-n7';
  const iconSize = variant === 'minimal' ? 20 : variant === 'homeWithIcon' ? 16 : 24;
  const iconMargin = variant === 'minimal' ? 8 : 12;
  const onHome = variant === 'home';
  const segmentVariant =
    variant === 'minimal'
      ? 'body3'
      : onHome
      ? LIBRARY_OBJECT_TOOLTIP_VARIANT_STYLES.home.typographyVariant
      : 'body2';

  return (
    <Box data-component="SegmentName" marginTop={marginTop} className="fs-unmask">
      <Flex justifyContent="flex-start">
        {variant !== 'home' && (
          <>
            <Box marginRight={iconMargin} flexShrink={variant === 'minimal' ? undefined : 0}>
              <DeprecatedIcon name="segments" color={iconColor} size={iconSize} />
            </Box>
          </>
        )}
        <Flex justifyContent="flex-start" column={false} className={css.flexChild}>
          <SegmentList
            segmentLink={segmentLink}
            segments={segments}
            variant={segmentVariant}
            color={textColor}
            wrapSingleSegment={wrapSingleSegment}
            ellipsize={onHome || ellipsize}
          />
          {onHome && segments[0]?.segment?.metadata?.archived && <ArchiveIndicator />}
        </Flex>
      </Flex>
    </Box>
  );
};

interface SegmentListProps
  extends Pick<React.ComponentProps<typeof Typography>, 'color' | 'variant'>,
    Pick<SegmentNameProps, 'wrapSingleSegment'> {
  segments: SegmentDetail[];
  segmentLink?: (segment: SegmentDetail, index: number) => string;
  ellipsize: boolean;
}
const SegmentList: React.FC<SegmentListProps> = ({
  color,
  variant,
  segmentLink,
  segments,
  wrapSingleSegment,
  ellipsize,
}) => {
  // if the name is not wrapped (eg Tooltip), the list of segment names can be a single text element.
  const wrap: SingleSegmentWrapper = wrapSingleSegment ?? identity;
  const flattenList = !wrapSingleSegment;

  const list = (
    <>
      {segments.map((segment, i) => {
        const singleSegment = (
          <SingleSegment
            segment={segment}
            lastSegment={i === segments.length - 1}
            color={color}
            variant={variant}
            key={segment.segment!.segment!.id}
            ellipsize={flattenList ? false : ellipsize}
            tag={flattenList ? 'span' : undefined}
          />
        );
        return wrap(singleSegment, segment, i, segmentLink);
      })}
    </>
  );

  if (flattenList) {
    return (
      <Text appearance="bodySmall" noWrap>
        {list}
      </Text>
    );
  }

  return list;
};
interface SingleSegmentProps
  extends Pick<React.ComponentProps<typeof Typography>, 'color' | 'variant'> {
  segment: SegmentDetail;
  lastSegment: boolean;
  ellipsize: boolean;
  tag?: React.ComponentProps<typeof Typography>['tag'];
}
const SingleSegment: React.FC<SingleSegmentProps> = ({
  segment,
  lastSegment,
  variant,
  color,
  ellipsize,
  tag = 'div',
}) => (
  <Typography
    data-component="SingleSegment"
    variant={variant}
    color={color}
    tag={tag}
    lineHeight={1.8}
    ellipsize={ellipsize}
  >
    {segment.segment!.segment!.name}
    {!lastSegment && <span>,&nbsp;</span>}
  </Typography>
);
